import React, { useState } from 'react';

const DAYS = [
  { name: 'Monday', short: 'M', color: '#3b82f6', field: 'mondayXp', formField: 'monday_xp' },
  { name: 'Tuesday', short: 'T', color: '#10b981', field: 'tuesdayXp', formField: 'tuesday_xp' },
  { name: 'Wednesday', short: 'W', color: '#8b5cf6', field: 'wednesdayXp', formField: 'wednesday_xp' },
  { name: 'Thursday', short: 'T', color: '#f97316', field: 'thursdayXp', formField: 'thursday_xp' },
  { name: 'Friday', short: 'F', color: '#6366f1', field: 'fridayXp', formField: 'friday_xp' },
  { name: 'Saturday', short: 'S', color: '#ec4899', field: 'saturdayXp', formField: 'saturday_xp' },
  { name: 'Sunday', short: 'S', color: '#ef4444', field: 'sundayXp', formField: 'sunday_xp' }
];

const DEFAULT_MAX_XP = Infinity;

const AdvancedModeInput = ({ weekdayXp, onChange, t, maxXp = DEFAULT_MAX_XP, childId, dashboardPath = '/', onUpgrade }) => {
  // User is "free" if they have a finite limit (subscribed users have Infinity)
  const isFreeUser = isFinite(maxXp);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const handleDayChange = (field, value) => {
    const parsedValue = parseInt(value, 10) || 0;
    // Clamp value to maxXp limit only for free users (finite limit)
    const clampedValue = isFinite(maxXp) ? Math.min(parsedValue, maxXp) : parsedValue;
    onChange({
      ...weekdayXp,
      [field]: clampedValue
    });
  };

  return (
    <div className="mb-4 pb-4 border-bottom" data-testid="advanced-mode-section">
      <div className="card border-0" style={{ backgroundColor: '#fef9e7' }}>
        <div className="card-body p-4">
          <label className="form-label fw-semibold">
            <i className="bi bi-trophy text-warning me-1"></i>
            {t('xpTarget')}
            {isFreeUser && (
              <span
                className="badge bg-secondary ms-2"
                style={{ fontSize: '0.6em', verticalAlign: 'middle', cursor: 'pointer' }}
                onClick={() => setShowUpgradeModal(true)}
                data-testid="max-xp-badge"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setShowUpgradeModal(true)}
              >
                <i className="bi bi-lock-fill me-1"></i>
                Max {maxXp} XP
              </span>
            )}
          </label>
          <p className="text-muted small mb-3">
            {t('selectDailyTarget')}
          </p>

          {/* Table Header */}
          <div className="row fw-bold mb-3 pb-2 border-bottom">
            <div className="col-4">{t('day')}</div>
            <div className="col-8">{t('xpGoal')}</div>
          </div>

          {/* Days */}
          {DAYS.map((day) => (
            <div className="row align-items-center mb-3" key={day.name}>
              <div className="col-4">
                <div className="d-flex align-items-center">
                  <span
                    className="badge rounded-circle d-inline-flex align-items-center justify-content-center me-3"
                    style={{
                      width: '32px',
                      height: '32px',
                      backgroundColor: day.color,
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                    data-testid={`${day.name.toLowerCase()}-badge`}
                  >
                    {day.short}
                  </span>
                  <span>{t(day.name.toLowerCase())}</span>
                </div>
              </div>
              <div className="col-8">
                <input
                  type="number"
                  id={`child_daily_target_${day.formField}`}
                  name={`child_daily_target[${day.formField}]`}
                  className="form-control"
                  min="100"
                  {...(isFinite(maxXp) ? { max: maxXp } : {})}
                  step="100"
                  value={weekdayXp[day.field]}
                  onChange={(e) => handleDayChange(day.field, e.target.value)}
                  style={{
                    height: '50px',
                    border: '2px solid #fbbf24',
                    borderRadius: '8px'
                  }}
                  data-testid={`${day.name.toLowerCase()}-xp-input`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowUpgradeModal(false)}
          data-testid="upgrade-modal"
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-star-fill text-warning me-2"></i>
                  {t('upgradeModalTitle')}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowUpgradeModal(false)}
                  data-testid="upgrade-modal-close"
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-0">
                  {t('upgradeModalDescription')}
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowUpgradeModal(false)}
                >
                  {t('upgradeModalClose')}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  data-testid="upgrade-button"
                  onClick={() => {
                    setShowUpgradeModal(false);
                    if (onUpgrade) {
                      onUpgrade();
                    } else {
                      window.location.href = `${dashboardPath}?upgrade=${childId}`;
                    }
                  }}
                >
                  <i className="bi bi-arrow-up-circle me-2"></i>
                  {t('upgradeModalUpgrade')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedModeInput;
